import type { ReactNode } from "react";
import type { InsertMode } from "./tableLinking";
import { testIds } from "@/testIds";

/**
 * The small pieces of the insert sheet: the glyphs, the section labels, the
 * Link / Copy cards and the keyboard bar. Sizes, spacing and copy are Olivia's
 * (design-handoffs/2026-09-27-tables-modern, 1a and 1b).
 */

/** A table, drawn small: a header band over a body. */
export function TableGlyph({ on, size = "row" }: { on?: boolean; size?: "row" | "slot" }) {
  const slot = size === "slot";
  return (
    <div
      aria-hidden
      className="grid shrink-0 overflow-hidden"
      style={{
        width: slot ? 22 : 38,
        height: slot ? 14 : 30,
        borderRadius: slot ? 2 : 5,
        gridTemplateRows: slot ? "4px 1fr" : "8px 1fr",
        background: "var(--lilia-bg)",
        border: `${slot ? 1.5 : 1}px solid ${on || slot ? "var(--lilia-primary)" : "var(--lilia-border-strong)"}`,
      }}
    >
      <div
        style={{
          background: slot ? undefined : on ? "var(--lilia-primary-subtle)" : "var(--lilia-bg-secondary)",
          borderBottom: `1px solid ${on || slot ? "var(--lilia-primary)" : "var(--lilia-border)"}`,
        }}
      />
      <div />
    </div>
  );
}

/** A paper, drawn small: a title line over three lines of text. */
export function PaperGlyph({ on, small }: { on?: boolean; small?: boolean }) {
  return (
    <div
      aria-hidden
      className="flex shrink-0 flex-col"
      style={{
        width: small ? 24 : 26,
        height: small ? 30 : 34,
        gap: 2,
        padding: "5px 4px",
        borderRadius: 3,
        background: "var(--lilia-bg)",
        border: `1px solid ${on ? "var(--lilia-primary)" : "var(--lilia-border-strong)"}`,
      }}
    >
      <div style={{ height: 2, background: on ? "var(--lilia-primary)" : "var(--lilia-text-muted)" }} />
      {[0, 1, 2].map((i) => (
        <div key={i} style={{ height: 1.5, background: on ? "var(--lilia-border-strong)" : "var(--lilia-border)" }} />
      ))}
    </div>
  );
}

/** "RECENT", "ALL TABLES · 12", "ALREADY USED IN". */
export function SheetLabel({ children, className = "" }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={`text-[10.5px] font-bold uppercase text-[color:var(--lilia-text-secondary)] ${className}`}
      style={{ letterSpacing: ".07em" }}
    >
      {children}
    </div>
  );
}

/** A key, as the sheet names it: ↵, ⇥, L. */
export function Key({ children }: { children: ReactNode }) {
  return (
    <b className="font-semibold text-[color:var(--lilia-text-secondary)]" style={{ fontFamily: "var(--lilia-font-mono)" }}>
      {children}
    </b>
  );
}

/** One of the two answers: a card with its consequence and a picture of it. */
function ChoiceCard({
  how, on, title, keyName, consequence, onPick,
}: {
  how: InsertMode;
  on: boolean;
  title: string;
  keyName?: string;
  consequence: ReactNode;
  onPick: () => void;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={on}
      data-testid={how === "link" ? testIds.tableTool.insertChoiceLink : testIds.tableTool.insertChoiceCopy}
      data-selected={on || undefined}
      onClick={onPick}
      className="flex min-w-0 items-center gap-3 text-left transition-colors"
      style={{
        padding: "10px 14px",
        borderRadius: 10,
        border: on ? "1.5px solid var(--lilia-primary)" : "1px solid var(--lilia-border-strong)",
        background: on ? "var(--lilia-primary-subtle)" : "var(--lilia-bg)",
      }}
    >
      <span
        aria-hidden
        className="shrink-0"
        style={{
          width: 16,
          height: 16,
          borderRadius: 8,
          background: "var(--lilia-bg)",
          border: on ? "5px solid var(--lilia-primary)" : "1.5px solid var(--lilia-border-strong)",
        }}
      />
      <span className="min-w-0 flex-1">
        <span className="block text-[13px] font-semibold text-[color:var(--lilia-text)]">
          {title}{" "}
          {keyName && (
            <span className="text-[10.5px] font-medium text-[color:var(--lilia-text-secondary)]" style={{ fontFamily: "var(--lilia-font-mono)" }}>
              {keyName}
            </span>
          )}
        </span>
        <span className="block text-[11.5px] leading-[1.5] text-[color:var(--lilia-text-secondary)]">{consequence}</span>
      </span>
      {how === "link" ? <LinkPicture /> : <CopyPicture />}
    </button>
  );
}

/** One table, many papers. */
function LinkPicture() {
  const ink = "var(--lilia-primary)";
  return (
    <span aria-hidden className="flex shrink-0 items-center gap-1">
      <span style={{ width: 14, height: 12, border: `1.5px solid ${ink}`, borderRadius: 2 }} />
      <span className="flex flex-col gap-[2px]">
        {[0, 1, 2].map((i) => <span key={i} style={{ width: 16, height: 1.5, background: ink }} />)}
      </span>
      <span className="flex flex-col gap-[2px]">
        {[0, 1, 2].map((i) => <span key={i} style={{ width: 9, height: 6, border: `1px solid ${ink}` }} />)}
      </span>
    </span>
  );
}

/** A table, and a separate one of its own. */
function CopyPicture() {
  const ink = "var(--lilia-text-muted)";
  return (
    <span aria-hidden className="flex shrink-0 items-center gap-[5px]">
      <span style={{ width: 14, height: 12, border: `1.5px solid ${ink}`, borderRadius: 2 }} />
      <span style={{ width: 10, height: 1.5, background: ink, opacity: 0.5 }} />
      <span style={{ width: 14, height: 12, border: `1.5px dashed ${ink}`, borderRadius: 2 }} />
    </span>
  );
}

/** Link and Copy as a decision with a picture and its consequence. Link is preselected. */
export function ChoiceCards({
  choice, onChoice, link, copy, keys,
}: {
  choice: InsertMode;
  onChoice: (c: InsertMode) => void;
  link: ReactNode;
  copy: ReactNode;
  /** Show the L / C key names on the cards. */
  keys?: boolean;
}) {
  return (
    <div role="radiogroup" aria-label="Link or copy" className="grid min-w-0 flex-1 gap-2.5" style={{ gridTemplateColumns: "1fr 1fr" }}>
      <ChoiceCard how="link" on={choice === "link"} title="Link" keyName={keys ? "L" : undefined} consequence={link} onPick={() => onChoice("link")} />
      <ChoiceCard how="copy" on={choice === "copy"} title="Copy" keyName={keys ? "C" : undefined} consequence={copy} onPick={() => onChoice("copy")} />
    </div>
  );
}

/** ↑↓ choose · ⇥ place · L / C link or copy · ↵ insert · esc close */
export function KeyBar({ onClose, children }: { onClose: () => void; children?: ReactNode }) {
  return (
    <div
      className="flex h-[30px] shrink-0 items-center gap-[18px] border-t border-[var(--lilia-border)] px-[18px] text-[11px] text-[color:var(--lilia-text-secondary)]"
      style={{ background: "var(--lilia-bg-secondary)" }}
    >
      <span><Key>↑↓</Key> choose</span>
      <span><Key>⇥</Key> place</span>
      <span><Key>L</Key> / <Key>C</Key> link or copy</span>
      <span><Key>↵</Key> insert</span>
      <button
        type="button"
        data-testid={testIds.tableTool.insertClose}
        onClick={onClose}
        className="rounded-[4px] hover:text-[color:var(--lilia-text)]"
      >
        <Key>esc</Key> close
      </button>
      <div className="flex-1" />
      {children}
    </div>
  );
}

/** The row every list in the sheet uses: selected is the accent wash and bar. */
export function sheetRowStyle(on: boolean): React.CSSProperties {
  return on
    ? { background: "var(--lilia-primary-subtle)", boxShadow: "inset 2px 0 0 var(--lilia-primary)" }
    : {};
}
